import { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Input, InputNumber, Segmented, Select, Tag, Upload } from "antd";
import { FileUp, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { readWorkbook, reshapeSource, TYPE_OPTIONS_LIST } from "../../shared/sourceFiles";
import { DeleteSource } from "../DeleteControls";

const SourceInspector = ({ source, file, issues = [], dispatch, hasSteps }) => {
  const update = (changes) => dispatch({ type: "updateSource", id: source.id, changes });
  const reshape = (settings, newFile) =>
    reshapeSource(dispatch, source, file, settings, newFile).catch((error) => toast.error(`Could not read the file: ${error.message}`));

  // The header settings as last chosen. The source only changes once the data
  // worker has re-read the file, so quick clicks build on these instead.
  const [header, setHeader] = useState({ hasHeader: source.hasHeader, headerRow: source.headerRow });
  useEffect(() => setHeader({ hasHeader: source.hasHeader, headerRow: source.headerRow }), [source.id, source.hasHeader, source.headerRow]);
  const changeHeader = (changes) => {
    const next = { ...header, ...changes };
    setHeader(next);
    reshape(next);
  };

  const replaceFile = async (picked) => {
    try {
      const workbook = await readWorkbook(picked);
      // Once steps exist, the saved columns are what they expect: keep them
      // and check the new file against them. Before that, take the new file's.
      if (hasSteps) {
        dispatch({ type: "attachSample", id: source.id, file: workbook });
        return false;
      }
      const sheetName = workbook.sheets.some((s) => s.name === source.sheetName) ? source.sheetName : workbook.sheets[0].name;
      await reshape({ sheetName }, { ...workbook, sheetName });
    } catch (error) {
      toast.error(`Could not read ${picked.name}: ${error.message}`);
    }
    return false;
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-600">{source.id.replace("source", "Source ")}</div>
        <Input
          variant="borderless"
          className="!px-0 text-lg font-semibold"
          value={source.label}
          onChange={(e) => update({ label: e.target.value })}
        />
        <p className="text-xs text-gray-500">{source.originalName}</p>
      </div>

      {!file && (
        <Alert type="info" showIcon message="Sample data isn't saved with the workflow. Upload a file to preview results." />
      )}
      {issues.map((issue) => (
        <Alert key={issue.message} type={issue.level === "error" ? "error" : issue.level === "warning" ? "warning" : "info"} showIcon message={issue.message} />
      ))}

      <Upload accept=".csv,.xlsx,.txt" showUploadList={false} beforeUpload={replaceFile}>
        <Button icon={<FileUp size={14} />}>{file ? "Replace sample file" : "Upload sample file"}</Button>
      </Upload>

      {file?.sheets.length > 1 && (
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Sheet</div>
          <Select className="w-full" value={file.sheetName} onChange={(sheetName) => reshape({ sheetName })} options={file.sheets.map((s) => ({ value: s.name, label: s.name }))} />
        </div>
      )}

      {/* Typing a row makes it the header; "No header" names columns by letter. */}
      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Header row</div>
        <div className="flex items-center gap-3">
          <InputNumber
            className="flex-1"
            min={1}
            precision={0}
            placeholder="None"
            value={header.hasHeader ? header.headerRow : null}
            onChange={(headerRow) => headerRow && changeHeader({ hasHeader: true, headerRow })}
          />
          <Checkbox
            checked={!header.hasHeader}
            onChange={(e) => changeHeader(e.target.checked ? { hasHeader: false, headerRow: 1 } : { hasHeader: true })}
          >
            <span className="text-xs text-gray-600">No header</span>
          </Checkbox>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          {header.hasHeader
            ? `Column names come from row ${header.headerRow}; data starts below it.`
            : "Columns are named by letter (A, B, C…)."}
        </p>
      </div>

      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">When running on a new file, find columns by</div>
        <Segmented
          block
          value={source.matchBy}
          onChange={(matchBy) => update({ matchBy })}
          options={[
            { value: "header", label: "Header name", disabled: !source.hasHeader },
            { value: "position", label: "Position (A, B, C…)" },
          ]}
        />
      </div>

      <div>
        <div className="text-xs font-medium text-gray-600 mb-1">Columns ({source.columns.length})</div>
        <div className="max-h-72 overflow-auto rounded-md border border-gray-200 divide-y">
          {source.columns.map((column, index) => (
            <div key={column.name} className="flex items-center gap-2 px-2 py-1 text-xs">
              <Tag className="!m-0 w-8 text-center">{column.letter}</Tag>
              <span className="flex-1 truncate" title={column.name}>
                {column.header ?? <span className="text-gray-400">no header</span>}
              </span>
              <Select
                size="small"
                className="w-24"
                value={column.type}
                options={TYPE_OPTIONS_LIST}
                onChange={(type) =>
                  update({ columns: source.columns.map((c, i) => (i === index ? { ...c, type } : c)) })
                }
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 border-t border-gray-100 pt-4">
        <DeleteSource source={source}>
          <Button danger icon={<Trash2 size={14} />}>Remove source</Button>
        </DeleteSource>
      </div>
    </div>
  );
};

export default SourceInspector;
